import { useState, useCallback } from "react";
import { getResourceFile } from "api/layer";
import { AppAction } from "helpers/context";

interface UseFileDownloadOptions {
  dispatch: React.Dispatch<any>;
}

interface UseFileDownloadReturn {
  downloadingFile: string | null;
  handleDownloadFile: (filePath: string, fileName: string) => Promise<void>;
}

/**
 * Custom hook for downloading release files
 */
export const useFileDownload = ({
  dispatch,
}: UseFileDownloadOptions): UseFileDownloadReturn => {
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  const handleDownloadFile = useCallback(
    async (filePath: string, fileName: string) => {
      if (!filePath) return;
      setDownloadingFile(filePath);
      try {
        const res: any = await getResourceFile(filePath);
        if (res?.data) {
          const base64Data = res.data;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray]);
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Failed to download file:", error);
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "downloadFile",
            message: "downloadFileError",
            titleIconColor: "yellow",
            titleIcon: "warning",
          },
        });
      } finally {
        setDownloadingFile(null);
      }
    },
    [dispatch]
  );

  return { downloadingFile, handleDownloadFile };
};
